import { getCookieValue } from './handleCookies';
import { useCookieConsent} from '@/services/cookieConsent';

// Cookie consent management
const { ConsentStatus, COOKIE_CONSENT_KEY } = useCookieConsent();

/**
 * Analytics Service
 * Manages Google Analytics and Google Tag Manager
 */

export class AnalyticsService {
  private static instance: AnalyticsService;
  private isInitialized = false;
  private isDisabled = false;

  private constructor() {
    // Check if cookies were declined on load
    this.checkInitialConsent();
  }

  /**
   * Check initial cookie consent and block tracking if declined
   */
  private checkInitialConsent() {
    const consent = getCookieValue(COOKIE_CONSENT_KEY);
    if (consent === ConsentStatus.DECLINE || consent === ConsentStatus.NOT_SET) {
      this.blockAllTracking();
    }
  }

  /**
   * Block all Google Analytics tracking IDs before they load
   */
  private blockAllTracking() {
    this.isDisabled = true;
  }

  /**
   * Get singleton instance
   */
  static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  /**
   * Initialize Google Tag Manager
   */
  initGTM() {
    if (this.isInitialized || this.isDisabled) return;

    const gtmId = 'GTM-WLG4W89';

    // Load GTM script
    (function(w: any, d: Document, s: string, l: string, i: string) {
      w[l] = w[l] || [];
      w[l].push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
      const f = d.getElementsByTagName(s)[0];
      const j = d.createElement(s) as HTMLScriptElement;
      const dl = l != 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode?.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', gtmId);

    // Add noscript iframe for GTM
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    this.isInitialized = true;
  }

  /**
   * Initialize Google Analytics
   */
  initGA() {
    if (this.isDisabled) return;

    const gaId = 'UA-169728429-2';

    // Load gtag.js script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(script);

    // Initialize gtag
    (window as any).dataLayer = (window as any).dataLayer || [];
    const gtag = (...args: any[]) => {
      (window as any).dataLayer.push(args);
    };
    (window as any).gtag = gtag;

    gtag('js', new Date());
    gtag('config', gaId);
  }

  /**
   * Enable all analytics services
   */
  enable() {
    // Reset disable flags
    this.isDisabled = false;

    this.initGTM();
    this.initGA();
  }

  /**
   * Disable all analytics services
   */
  disable() {
    this.blockAllTracking();
  }
}

export default AnalyticsService.getInstance();

